import { useState, useEffect } from "react";

import {
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

import apiService from "../services/api";

const PredefinedReplySelector = ({ onSelect, ticket, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groupedReplies, setGroupedReplies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedReply, setSelectedReply] = useState(null);

  useEffect(() => {
    if (isOpen && groupedReplies.length === 0) {
      fetchReplies();
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        if (selectedReply) {
          setSelectedReply(null);
        } else {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, selectedReply]);

  const fetchReplies = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPredefinedRepliesGrouped();

      if (response.status === 200) {
        setGroupedReplies(response.data || []);
        // Expand only the first category by default (accordion behavior)
        if (response.data.length > 0) {
          const firstCategoryId =
            response.data[0].category_id || "uncategorized";

          setExpandedCategories({ [firstCategoryId]: true });
        }
      }
    } catch (error) {
      console.error("Failed to fetch predefined replies:", error);
    } finally {
      setLoading(false);
    }
  };

  const replaceVariables = (content) => {
    if (!content || !ticket) return content;

    let result = content;

    // Customer info
    const customerName = ticket.customer?.name || ticket.customer?.email || "";
    const nameParts = customerName.split(" ");
    const firstName = nameParts[0] || customerName;
    const lastName = nameParts.slice(1).join(" ") || "";

    result = result.replace(/\[FIRSTNAME\]/g, firstName);
    result = result.replace(/\[LASTNAME\]/g, lastName);
    result = result.replace(/\[EMAIL\]/g, ticket.customer?.email || "");
    result = result.replace(/\[TICKET_NUMBER\]/g, ticket.ticketNumber || "");
    result = result.replace(/\[TICKET_SUBJECT\]/g, ticket.subject || "");

    return result;
  };

  const handleSelectReply = async (reply) => {
    const processedContent = replaceVariables(reply.content);

    onSelect(processedContent);
    setIsOpen(false);
    setSearchQuery("");
    setSelectedReply(null);

    // Track usage
    try {
      await apiService.usePredefinedReply(reply.reply_id);
    } catch (error) {
      // Silently fail - usage tracking is not critical
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => {
      // Accordion behavior: if clicking the open one, close it; otherwise open only this one
      if (prev[categoryId]) {
        return {}; // Close all
      }

      return { [categoryId]: true }; // Open only this one
    });
  };

  // Filter replies based on search
  const filteredGroups = groupedReplies
    .map((group) => ({
      ...group,
      replies: group.replies.filter(
        (reply) =>
          !searchQuery ||
          reply.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          reply.content.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((group) => group.replies.length > 0);

  return (
    <>
      <button
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
          disabled
            ? "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
            : "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50"
        }`}
        disabled={disabled}
        title="Insert predefined reply"
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
      >
        <DocumentTextIcon className="w-4 h-4" />
        <span className="hidden sm:inline">Quick Reply</span>
        <ChevronDownIcon className="w-4 h-4" />
      </button>

      {/* Modal Overlay - Using Portal to render at document body */}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
              {/* Backdrop */}
              <motion.div
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-black/50"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                onClick={() => {
                  setIsOpen(false);
                  setSelectedReply(null);
                }}
              />

              {/* Modal */}
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="relative w-full max-w-3xl max-h-[90vh] sm:max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden flex flex-col"
                exit={{ opacity: 0, scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.95 }}
              >
                {/* Header */}
                <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    {/* Mobile: Show back button when reply is selected */}
                    <div className="flex items-center gap-2">
                      {selectedReply && (
                        <button
                          className="md:hidden p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          onClick={() => setSelectedReply(null)}
                        >
                          <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
                        </button>
                      )}
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedReply && window.innerWidth < 768
                          ? selectedReply.title
                          : "Predefined Replies"}
                      </h3>
                    </div>
                    <button
                      className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      onClick={() => {
                        setIsOpen(false);
                        setSelectedReply(null);
                      }}
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  {/* Hide search when viewing reply on mobile */}
                  {(!selectedReply || window.innerWidth >= 768) && (
                    <div className="relative">
                      <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        autoFocus
                        className="w-full pl-10 pr-4 py-2 sm:py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Search replies..."
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {/* Content - Split View on desktop, stacked on mobile */}
                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                  {/* Left Panel - Categories and Replies List */}
                  {/* Hide on mobile when reply is selected */}
                  <div
                    className={`${
                      selectedReply ? "hidden md:flex" : "flex"
                    } flex-col w-full md:w-1/2 md:border-r border-gray-200 dark:border-gray-700 overflow-y-auto`}
                  >
                    {loading ? (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-3" />
                        Loading replies...
                      </div>
                    ) : filteredGroups.length === 0 ? (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        {searchQuery
                          ? "No replies match your search"
                          : "No predefined replies available"}
                      </div>
                    ) : (
                      <div className="py-2">
                        {filteredGroups.map((group) => (
                          <div key={group.category_id || "uncategorized"}>
                            {/* Category Header */}
                            <button
                              className="w-full flex items-center gap-2 px-3 sm:px-4 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                              onClick={() =>
                                toggleCategory(
                                  group.category_id || "uncategorized"
                                )
                              }
                            >
                              <ChevronRightIcon
                                className={`w-4 h-4 transition-transform ${
                                  expandedCategories[
                                    group.category_id || "uncategorized"
                                  ]
                                    ? "rotate-90"
                                    : ""
                                }`}
                              />
                              <FolderIcon className="w-4 h-4 text-yellow-500" />
                              <span className="flex-1 truncate">
                                {group.name}
                              </span>
                              <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                {group.replies.length}
                              </span>
                            </button>

                            {/* Replies */}
                            <AnimatePresence>
                              {expandedCategories[
                                group.category_id || "uncategorized"
                              ] && (
                                <motion.div
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  initial={{ height: 0, opacity: 0 }}
                                >
                                  {group.replies.map((reply) => (
                                    <button
                                      className={`w-full text-left px-3 sm:px-4 py-2.5 pl-9 sm:pl-11 transition-colors group ${
                                        selectedReply?.reply_id ===
                                        reply.reply_id
                                          ? "bg-purple-100 dark:bg-purple-900/40"
                                          : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                                      }`}
                                      key={reply.reply_id}
                                      onClick={() => setSelectedReply(reply)}
                                      onDoubleClick={() =>
                                        handleSelectReply(reply)
                                      }
                                    >
                                      <div className="flex items-start gap-2">
                                        <DocumentTextIcon
                                          className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                                            selectedReply?.reply_id ===
                                            reply.reply_id
                                              ? "text-purple-600 dark:text-purple-400"
                                              : "text-gray-400 group-hover:text-purple-500"
                                          }`}
                                        />
                                        <div className="flex-1 min-w-0">
                                          <p
                                            className={`text-sm font-medium truncate ${
                                              selectedReply?.reply_id ===
                                              reply.reply_id
                                                ? "text-purple-700 dark:text-purple-300"
                                                : "text-gray-900 dark:text-white group-hover:text-purple-700 dark:group-hover:text-purple-300"
                                            }`}
                                          >
                                            {reply.title}
                                          </p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                            {reply.content.substring(0, 50)}...
                                          </p>
                                        </div>
                                        {/* Mobile: Show arrow indicator */}
                                        <ChevronRightIcon className="w-4 h-4 text-gray-400 md:hidden flex-shrink-0" />
                                      </div>
                                    </button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right Panel - Preview */}
                  {/* Show full width on mobile when reply is selected */}
                  <div
                    className={`${
                      selectedReply ? "flex" : "hidden md:flex"
                    } flex-col w-full md:w-1/2 overflow-hidden bg-gray-50 dark:bg-gray-900/50`}
                  >
                    {selectedReply ? (
                      <>
                        {/* Hide title on mobile since it's in header */}
                        <div className="hidden md:block p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            {selectedReply.title}
                          </h4>
                          {selectedReply.variables &&
                            selectedReply.variables.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {selectedReply.variables.map((v) => (
                                  <span
                                    className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs rounded"
                                    key={v}
                                  >
                                    [{v}]
                                  </span>
                                ))}
                              </div>
                            )}
                        </div>
                        {/* Mobile: Show variables at top */}
                        {selectedReply.variables &&
                          selectedReply.variables.length > 0 && (
                            <div className="md:hidden p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                              <div className="flex flex-wrap gap-1">
                                {selectedReply.variables.map((v) => (
                                  <span
                                    className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs rounded"
                                    key={v}
                                  >
                                    [{v}]
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        <div className="flex-1 p-3 sm:p-4 overflow-y-auto">
                          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            {replaceVariables(selectedReply.content)}
                          </div>
                        </div>
                        <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                          <button
                            className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                            onClick={() => handleSelectReply(selectedReply)}
                          >
                            Insert Reply
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">
                        <div className="text-center p-4">
                          <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>Select a reply to preview</p>
                          <p className="text-xs mt-1">Double-click to insert</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer - Hide on mobile when viewing reply */}
                <div
                  className={`${
                    selectedReply ? "hidden md:block" : "block"
                  } p-2 sm:p-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-900/50`}
                >
                  <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                    Variables like [FIRSTNAME] will be automatically replaced
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default PredefinedReplySelector;
